// EventShortList.tsx
import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Divider, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { api } from "../../../api/eventApi";
import { api as groupApi } from "../../../api/groupApi";
import { EventRecord } from "../../../types/event.ts";
import { useFetch } from "../../../hooks/useFetch2";
import { useTranslation } from 'react-i18next';
import { Plate } from '../../../types/plate.ts';
// import LocalizedGrid from '../../localizedGrid/LocalizedGrid.tsx';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3.tsx';
import i18n from '../../../i18n';
import dayjs from 'dayjs';
import { formatDateTime } from '../../../tools/util.ts';
import { Group } from '../../../types/group.ts';

interface EventShortListProps {
    plate?: Plate | null;
}

export const EventShortList: React.FC<EventShortListProps> = ({ plate }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data, setData, error, update, loading } = useFetch<EventRecord[]>(api.getFiltered, [], {
        registration_number: plate?.registration_number || '',
        limit: 20,
    });
    const { data: groups } = useFetch<Group[]>(groupApi.get, []);
    const [isExporting, setIsExporting] = useState(false);

    const columns: GridColDef[] = [
        // { field: "id", headerName: "ID", type: "number", flex: 1, width: 50, },
        { field: "point_name", headerName: t('EventList.point_name'), type: "string", flex: 2, width: 150 },
        { field: "checkpoint_name", headerName: t('EventList.checkpoint_name'), type: "string", flex: 2, width: 150 },
        { field: "origin_name", headerName: t('EventList.origin_name'), type: "string", flex: 2, width: 150 },
        { field: "ts_start", headerName: t('EventList.ts_start'), type: "string", flex: 2, width: 200, valueFormatter: (v) => formatDateTime(v)},
        { field: "registration_number", headerName: t('EventList.registration_number'), type: "string", flex: 2, width: 100, },
        { field: "group_id", headerName: t('PlatesList.group'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: groups.map( group => ({value: group.group_id, label: group.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<Group>) => {
                return (
                    <Typography>
                        {groups.find(g => g.group_id === params.row.group_id)?.name}
                    </Typography>
                );
            }
        },
        { field: "car_brand", headerName: t('EventList.car_brand'), type: "string", flex: 2, width: 100, },
        { field: "car_owner", headerName: t('EventList.car_owner'), type: "string", flex: 2, width: 100, },
    ];

    // --- Мобильная карточка ---
    const renderMobileCard = (event: EventRecord) => (
        <Card key={event.id} sx={{ mb: 2, boxShadow: 1, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            {event.registration_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {event.ts_start}
                        </Typography>
                    </Box>
                    {/* <IconButton onClick={() => handleOpenModal(event)} size="small">
                        <Visibility color="action" />
                    </IconButton> */}
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('EventList.origin_name')}:</strong> {event.origin_name}</Typography>
                    <Typography variant="body2"><strong>{t('EventList.checkpoint_name')}:</strong> {event.checkpoint_name}</Typography>
                    <Typography variant="body2"><strong>{t('EventList.car_owner')}:</strong> {event.car_owner || '—'}</Typography>
                    <Typography variant="body2"><strong>{t('EventList.car_brand')}:</strong> {event.car_brand || '—'}</Typography>
                </Stack>
            </CardContent>
        </Card>
    );

    return (

    <Paper sx={{ p: 2, maxWidth: 1600, mx: "auto", mt: 0 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body1">
                {t('EventList.Events')}
            </Typography>
        </Box>

                {/* Контент: Таблица или Карточки */}
                {isMobile ? (
                    <Box>
                        {data.map(event => renderMobileCard(event))}
                        {data.length === 0 && <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>{t('common.no_data')}</Typography>}
                    </Box>
                ) : (
                    
        // <DataGrid<EventRecord>
        <LocalizedGrid
            gridId='EventShortList'
            lang={i18n.language as any}
            rows={data}
            columns={columns} 
            getRowId={(row) => row.id}
            loading={loading}
            columnVisibilityModel={{
                registration_number: !plate,
                car_brand: !plate,
                car_owner: !plate,
                ts_start: true,
            }}
            autoHeight
            sx={{ maxWidth: '1600px',
                width: '100%',
                // '& .MuiTablePagination-root': {
                // display: 'none',
                // },
            }}
            hideFooter
            hideToolbar={true}
        />
                )}

        <Typography color='error'>{error?.message}</Typography>
      
      </Paper>
    );
}